// ══════════════════════════════════════════════════════════════════════════════
//  #HR_DOCTILES_V1 — Nathan: "add more items to drivers records and ability to name
//  said [tile]... didn't mention a limit so could we simply have an add new button?
//  Could same tile house all updates ex: DL Copy, DL Renewal, MVR reports (same tile
//  but all updates)... the ability to add updates within a tile section named and
//  adding tile."
//
//  Replaces the old model of one fixed box per document type, one file per box
//  (a new upload silently overwrote whatever was there — no history, and no way to
//  add a box for something that wasn't DL / Med / MVR / OSHA / Drug / Photo / Other).
//
//  New model: `emp.docs` is a flat, APPEND-ONLY array. Every entry carries a `tile` id
//  ('dl', 'med', 'mvr', 'osha', 'photo', 'drug', 'other', or a 'custom-<ts>' id for a
//  tile someone typed a name for). The UI groups by `tile` and shows every entry —
//  nothing is ever overwritten or deleted, so a tile really does "house all updates."
//  A custom tile's display name rides along on every entry as `tileLabel` (redundant
//  by design — any single surviving entry is enough to reconstruct the tile's name).
//
//  #HR_DOCTILES_MIGRATE_V1 — pre-existing employees only have the OLD shapes: a fixed
//  `slots[]` array (index 0-5, one file each) and/or a flat, untagged `docs[]` list
//  (HR's old "Other documents" drop zone, no `tile` field). Both are read-only source
//  material here — `effectiveDocs()` synthesizes tile-tagged entries from them ON READ,
//  and only for a tile that has NO real (new-style) entries yet. Nothing is deleted or
//  rewritten until the user next uploads through the new UI, so this is fully reversible
//  and there is no one-time migration step to run or get wrong.
//
//  One extra wrinkle worth knowing about: index 3 of the old `slots[]` array collided —
//  HRModule called it "Other", SafetyModule's driver editor called the SAME index "MVR
//  Report" and silently overwrote whatever HR had there (or vice versa). There was no
//  way to tell them apart except the stored label, so migration sniffs that label to
//  route the file to the right tile instead of guessing.
// ══════════════════════════════════════════════════════════════════════════════

// #SAFETY_DRIVERDOCORDER_V1 (Aug 28) — Bill's 7-tile set for Safety's driver modal: Application,
// Copy of DL, Medical Card, Original MVR, Clearing House, Driver Certification, Annual MVR & Review
// (the last folds what would've been two separate annual items into one tile, per Bill's ask).
// dl/mvr keep their old ids (so existing uploaded docs stay attached) but got renamed labels.
export const BUILTIN_DOC_TILES = [
  { id: 'photo',         label: 'Photo' },
  { id: 'application',   label: 'Application' },
  { id: 'dl',            label: 'Copy of DL' },
  { id: 'med',           label: 'Medical Card' },
  { id: 'mvr',           label: 'Original MVR' },
  { id: 'clearinghouse', label: 'Clearing House' },
  { id: 'certification', label: 'Driver Certification' },
  { id: 'annualmvr',     label: 'Annual MVR & Review' },
  { id: 'osha',          label: 'OSHA Card' },
  { id: 'drug',          label: 'Drug Screen' },
  { id: 'other',         label: 'Other documents' },
];

// Old fixed-index slot order (HRModule's SLOT_DEFS, pre-#HR_DOCTILES_V1).
const LEGACY_SLOT_IDS = ['photo', 'dl', 'med', 'other', 'osha', 'drug'];

const legacySlotDocs = (e) => {
  const legacy = Array.isArray(e && e.slots) ? e.slots : [];
  const out = [];
  legacy.forEach((s, i) => {
    if (!s || !s.path) return;
    let id = LEGACY_SLOT_IDS[i] || null;
    if (i === 3 && /mvr/i.test(s.label || '')) id = 'mvr';   // #HR_DOCTILES_MIGRATE_V1 — index-3 collision
    if (!id) return;
    out.push({ id: `legacy-slot-${i}`, tile: id, path: s.path, name: s.name, type: s.type, at: s.at || '', legacy: true });
  });
  return out;
};

// Old flat, untagged "Other documents" list -> all route to the 'other' tile.
const legacyFlatDocs = (e) => (Array.isArray(e && e.docs) ? e.docs : [])
  .filter(d => d && !d.tile && d.path)
  .map(d => ({ ...d, tile: 'other', legacy: true }));

const taggedDocs = (e) => (Array.isArray(e && e.docs) ? e.docs : []).filter(d => d && d.tile);

// Every doc entry that should be considered "on file" for this employee, new-style and
// migrated-on-read legacy combined. New-style entries always win for a tile that has any.
export const effectiveDocs = (e) => {
  const own = taggedDocs(e);
  const haveTiles = new Set(own.map(d => d.tile));
  const legacy = [...legacySlotDocs(e), ...legacyFlatDocs(e)];
  return [...own, ...legacy.filter(ld => !haveTiles.has(ld.tile))];
};

const customTileLabel = (docsForTile) => {
  for (let i = docsForTile.length - 1; i >= 0; i--) {
    if (docsForTile[i].tileLabel) return docsForTile[i].tileLabel;
  }
  return '';
};

// Full tile list for rendering, oldest-first history per tile (so history[history.length-1]
// is always "current"). `only` (an array of builtin ids) narrows which BUILTIN boxes show —
// Safety's driver editor only wants dl/med/mvr/osha, not Photo/Drug Screen/Other documents.
// Custom tiles always show regardless of `only` — Nathan's whole point was drivers can get
// tiles the fixed catalog never anticipated.
export const docTilesFor = (e, only) => {
  const docs = effectiveDocs(e).slice().sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));
  const byTile = {};
  docs.forEach(d => { (byTile[d.tile] = byTile[d.tile] || []).push(d); });
  // #SAFETY_DRIVERDOCORDER_V1 — when `only` is given, ITS order wins (not BUILTIN_DOC_TILES's
  // definition order), so a caller like Safety's driver modal can reorder its own boxes.
  const catalog = only
    ? only.map(id => BUILTIN_DOC_TILES.find(t => t.id === id)).filter(Boolean)
    : BUILTIN_DOC_TILES;
  const tiles = catalog.map(t => ({ id: t.id, label: t.label, builtin: true, history: byTile[t.id] || [] }));
  Object.keys(byTile).forEach(id => {
    if (BUILTIN_DOC_TILES.some(t => t.id === id)) return;   // a builtin id, shown above or intentionally filtered by `only` — never custom
    const list = byTile[id];
    tiles.push({ id, label: customTileLabel(list) || id, builtin: false, history: list });
  });
  return tiles;
};

// Append one uploaded file to a tile. `label` is only meaningful for a NEW custom tile
// (id starting 'custom-') — it rides along on the entry so the tile's name survives even
// if this is its only doc. Returns the updated docs array; caller writes it into state.
// #DOC_STAMP_V1 (2026-10-21) — optional `who` (the signed-in upn) is stamped as `by`; `at` already rides on every upload.
export const appendTileDoc = (docsArr, tileId, tileLabel, file, who) => {
  const docs = Array.isArray(docsArr) ? docsArr : [];
  const entry = {
    id: 'doc-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6),
    tile: tileId,
    path: file.path, name: file.name, type: file.type, at: file.at,
  };
  if (String(tileId).startsWith('custom-') && tileLabel) entry.tileLabel = tileLabel;
  if (who) entry.by = String(who).toLowerCase();   // #DOC_STAMP_V1
  return [...docs, entry];
};

export const newCustomTileId = () => 'custom-' + Date.now();

// #DOCTILE_DELETE_V1 (2026-10-06) — Bill: "we will need the ability to delete (with a cheeky warning
// message)." A card got loaded into the driver file because the Training Matrix had nowhere to put it.
// Takes one entry out of the record and returns the fields to write back ({ docs, slots, docsRemoved }).
// The file itself is NOT erased from storage: the entry moves to `docsRemoved` with who/when, so a
// wrong click on a DOT driver file can be put back. Handles all three shapes an entry can have:
// new-style (has an id), a legacy fixed slot (id 'legacy-slot-N'), or an old untagged flat doc.
export const removeTileDoc = (emp, entry, who) => {
  const docs = Array.isArray(emp && emp.docs) ? emp.docs : [];
  const slots = Array.isArray(emp && emp.slots) ? emp.slots : [];
  const out = { docs, slots };
  if (!entry) return out;
  const m = /^legacy-slot-(\d+)$/.exec(String(entry.id || ''));
  if (m) out.slots = slots.map((s, i) => (i === Number(m[1]) ? {} : s));
  else out.docs = docs.filter(d => !(d && ((entry.id && d.id === entry.id) || (!d.id && d.path && d.path === entry.path))));
  const { legacy, ...kept } = entry;
  out.docsRemoved = [...(Array.isArray(emp && emp.docsRemoved) ? emp.docsRemoved : []), { ...kept, removedAt: new Date().toISOString(), removedBy: who || '' }];
  return out;
};
